<template>
<!-- 网页导航栏 -->
  <nav class="nav-header">
    <router-link to="/" class="logo">
      <img src="@/assets/logo.svg" alt="ctcsci" />
    </router-link>

    <div class="nav-menu">
      <div class="nav-item">
        <div class="dropdown-trigger" 
             :class="{ 'active': activeMenu === 'product' || currentSection === 'product' }"
             @click.stop="toggleMenu('product')"
             tabindex="0">
          {{ t('nav.products') }}
          <img :src="showProductMenu ? topIcon : bottomIcon" 
               class="dropdown-icon" 
               alt="dropdown" />
          <div class="dropdown-menu" 
               v-show="showProductMenu"
               @click.stop>
            <router-link @click="closeAllMenus" to="/product/ip-transit">{{ t('nav.ipTransit') }}</router-link>
            <router-link @click="closeAllMenus" to="/product/cloud-vm">{{ t('nav.cloudVM') }}</router-link>
            <router-link @click="closeAllMenus" to="/product/bare-metal">{{ t('nav.bareMetal') }}</router-link>
            <router-link @click="closeAllMenus" to="/product/sdwan">{{ t('nav.sdwan') }}</router-link>
          </div>
        </div>
      </div>
      <div class="nav-item">
        <div class="dropdown-trigger" 
             :class="{ 'active': activeMenu === 'network' || currentSection === 'network' }"
             @click.stop="toggleMenu('network')"
             tabindex="0">
          {{ t('nav.network') }}
          <img :src="showNetworkMenu ? topIcon : bottomIcon" 
               class="dropdown-icon" 
               alt="dropdown" />
          <div class="dropdown-menu" 
               v-show="showNetworkMenu"
               @click.stop>
            <router-link @click="closeAllMenus" to="/network/global-network">{{ t('nav.globalNetwork') }}</router-link>
            <router-link @click="closeAllMenus" to="/network/locations">{{ t('nav.locations') }}</router-link>
            <router-link @click="closeAllMenus" to="/network/latency">{{ t('nav.latency') }}</router-link>
            <router-link @click="closeAllMenus" to="/network/ddos-shield">{{ t('nav.ddosShield') }}</router-link>
          </div>
        </div>
      </div>
      <div class="nav-item">
        <div class="dropdown-trigger" 
             :class="{ 'active': activeMenu === 'company' || currentSection === 'company' }"
             @click.stop="toggleMenu('company')"
             tabindex="0">
          {{ t('nav.company') }}
          <img :src="showCompanyMenu ? topIcon : bottomIcon" 
               class="dropdown-icon" 
               alt="dropdown" />
          <div class="dropdown-menu" 
               v-show="showCompanyMenu"
               @click.stop>
            <router-link @click="closeAllMenus" to="/company/about">{{ t('nav.about') }}</router-link>
            <router-link @click="closeAllMenus" to="/company/careers">{{ t('nav.careers') }}</router-link>
          </div>
        </div>
      </div>
      <div class="nav-item">
        <router-link to="/pricing" class="nav-link" custom v-slot="{ href, navigate, isActive }">
          <a :href="href"
             @click="navigate"
             :class="{ 'active': isActive }"
             class="dropdown-trigger">
            {{ t('nav.pricing') }}
          </a>
        </router-link>
      </div>
    </div>

    <LanguageSelector />
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import bottomIcon from '@/assets/icon/bottom.png'
import topIcon from '@/assets/icon/top.png'
import LanguageSelector from '@/components/LanguageSelector.vue'
import { useTranslation } from '@/utils/i18n'

const route = useRoute()
const { t } = useTranslation()

const showProductMenu = ref(false)
const showNetworkMenu = ref(false)
const showCompanyMenu = ref(false)
const activeMenu = ref(null)

// 添加计算属性来判断当前路由是否匹配对应的菜单
const currentSection = computed(() => {
  const path = route.path
  if (path.startsWith('/product')) return 'product'
  if (path.startsWith('/network')) return 'network'
  if (path.startsWith('/company')) return 'company'
  return null
})

// 切换菜单显示状态
const toggleMenu = (menuType) => {
  if (menuType === 'product') {
    showProductMenu.value = !showProductMenu.value
    showNetworkMenu.value = false
    showCompanyMenu.value = false
    activeMenu.value = showProductMenu.value ? 'product' : null
  } else if (menuType === 'network') {
    showNetworkMenu.value = !showNetworkMenu.value
    showProductMenu.value = false
    showCompanyMenu.value = false
    activeMenu.value = showNetworkMenu.value ? 'network' : null
  } else if (menuType === 'company') {
    showCompanyMenu.value = !showCompanyMenu.value
    showProductMenu.value = false
    showNetworkMenu.value = false
    activeMenu.value = showCompanyMenu.value ? 'company' : null
  }
}

// 点击外部关闭菜单
const handleClickOutside = (event) => {
  const dropdowns = document.querySelectorAll('.dropdown-trigger')
  let clickedOutside = true
  
  dropdowns.forEach(dropdown => {
    if (dropdown.contains(event.target)) {
      clickedOutside = false
    }
  })

  if (clickedOutside) {
    closeAllMenus()
  }
}

// 添加关闭所有菜单的方法
const closeAllMenus = () => {
  showProductMenu.value = false
  showNetworkMenu.value = false
  showCompanyMenu.value = false
  activeMenu.value = null
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 100;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.logo img {
  width: auto;
  height: 30px;
}

.logo-text {
  color: #000000;
  font-size: 20px;
}

.nav-menu {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 30px;
}

.nav-item {
  position: relative;
  font-weight: 500;
}

.nav-item a {
  text-decoration: none;
  color: rgba(0,0,0,0.7);
}

.dropdown-trigger {
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  gap: 4px;
  padding: 8px 0;
  outline: none;
  color: #333;
  transition: color 0.3s ease;
  text-decoration: none;
}

.dropdown-trigger:hover,
.dropdown-trigger.active {
  color: #007AFF;
}

.dropdown-icon {
  width: 10px;
  height: 9.6px;
  margin-left: 4px;
}

.dropdown-menu {
  position: absolute;
  z-index: 1000;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 1px solid rgba(0,0,0,0.1);
  box-shadow: 0 10px 10px rgba(0,0,0,0.1);
  border-radius: 0px;
  padding: 10px 0;
  min-width: 200px;
}

.dropdown-menu a {
  display: block;
  padding: 15px 20px;
  color: #333;
  text-decoration: none;
  transition: all 0.3s ease;
}

.dropdown-menu a:hover {
  /* background: #e6f7ff; */
  color: #007AFF;
}

.dropdown-menu a:active {
  /* background: #bae7ff; */
  color: #007AFF;
}

.dropdown-menu a.router-link-active {
  /* background: #e6f7ff; */
  color: #007AFF;
}

.nav-link {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 8px 0;
  text-decoration: none;
}
</style> 